import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { fetchBlogs, setPage } from '../store/slices/blogSlice';
import Pagination from '../components/common/Pagination';
import SEO from '../components/SEO/SEO';
import { fetchSEO } from '../store/slices/seoSlice';

const BlogPage = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { items: blogs, pagination, loading, error } = useSelector((state) => state.blog);

  useEffect(() => {
    const page = parseInt(searchParams.get('page') || '1');
    const category = searchParams.get('category') || '';
    const search = searchParams.get('search') || '';

    dispatch(setPage(page));
    dispatch(fetchBlogs({ page, category, search }));
    dispatch(fetchSEO('blog'));
  }, [searchParams, dispatch]);

  const handlePageChange = (page) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <>
      <SEO
        page="blog"
        title="Blog - Art, Vastu & Home Decor Insights"
        description="Read our latest articles on art, Vastu, spiritual decor, and home design tips. Get insights on how to transform your space with premium art frames."
        keywords="blog, art blog, vastu tips, home decor tips, spiritual art, interior design"
        url="/blog"
      />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-bold text-black mb-2">Our Blog</h1>
          <p className="text-gray-600">Insights on art, Vastu, and home decor</p>
        </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600">Error loading blogs: {error}</p>
          <p className="text-sm text-red-500 mt-2">
            Please check your connection and ensure the backend is running.
          </p>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow p-4 animate-pulse">
              <div className="w-full h-48 bg-gray-200 rounded mb-4" />
              <div className="h-4 bg-gray-200 rounded mb-2" />
              <div className="h-4 bg-gray-200 rounded w-2/3" />
            </div>
          ))}
        </div>
      ) : blogs.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {blogs.map((blog) => (
              <Link
                key={blog._id}
                to={`/blog/${blog._id}`}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 group"
              >
                {blog.image && (
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={blog.image}
                      alt={blog.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                )}
                <div className="p-4">
                  <span className="inline-block px-3 py-1 bg-secondary-light text-primary rounded-full text-xs mb-2">
                    {blog.category}
                  </span>
                  <h3 className="font-semibold text-lg mb-2 line-clamp-2 group-hover:text-primary transition">
                    {blog.title}
                  </h3>
                  {blog.excerpt && (
                    <p className="text-gray-600 text-sm mb-3 line-clamp-3">
                      {blog.excerpt}
                    </p>
                  )}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{blog.author || 'Admin'}</span>
                    <span>{formatDate(blog.publishedAt || blog.createdAt)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
          />
        </>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg mb-2">No blog posts available yet. Check back soon!</p>
          {!error && (
            <p className="text-sm text-gray-500">
              Note: Only published blog posts are visible. Make sure blogs are marked as published in the admin panel.
            </p>
          )}
        </div>
      )}
      </div>
    </>
  );
};

export default BlogPage;

